/* ─────────────────────────────────────────────────────────────────────────────
   RVP with QC — mock data layer (aligned to Admin mockup v5).

   Backend contract (/api/qc/*) is NOT frozen yet, so the module runs against these
   in-memory shapes (rvpQcApiSlice flips on USE_MOCK). The data + mutations mirror the
   v5 mockup exactly: ProShip Master Parameters (the constant, courier-independent QC
   vocabulary), the Courier QC Master 3-step path, and the PCIS catalog couriers expose.
   ──────────────────────────────────────────────────────────────────────────── */

const clone = (x) => JSON.parse(JSON.stringify(x))

/* ── ProShip Master Parameters — the CONSTANT, courier-independent QC vocabulary.
   Merchants pick checks by these names; courier QC parameters map to these by id. */
let MASTER_PARAMS = [
  { id: 'img', name: 'Item Images (front/back)', desc: 'Front and back photos of the returned item.' },
  { id: 'dmg', name: 'Damage Check', desc: 'Agent confirms whether the item is physically damaged.' },
  { id: 'sku', name: 'SKU / EAN match', desc: 'Scanned/entered SKU or EAN matches the order.' },
  { id: 'brand', name: 'Brand Check', desc: 'Brand on the item matches the ordered brand.' },
  { id: 'qty', name: 'Quantity Check', desc: 'Number of units returned matches the order.' },
  { id: 'size', name: 'Size Match', desc: 'Item size matches the ordered size.' },
  { id: 'color', name: 'Colour Match', desc: 'Item colour matches the ordered colour.' },
  { id: 'seal', name: 'Tamper / Seal Intact', desc: 'Security seal / tamper-evident packaging is intact.' },
  { id: 'acc', name: 'Accessories Present', desc: 'All bundled accessories are present.' },
  { id: 'desc', name: 'Product Description', desc: 'Free-text product description captured at the doorstep.' },
]
export const mpById = (id) => MASTER_PARAMS.find((p) => p.id === id)
export const mpName = (id) => { const m = mpById(id); return m ? m.name : '(unmapped)' }

export const TYPE_LABEL = { standard: 'Standard QC', reason: 'Reason-based QC', nonqc: 'Non-QC' }

/* ── Merchant-level QC setups (global). type: standard | reason | nonqc ── */
let MERCHANTS = [
  {
    id: 'm_tss', name: 'The Souled Store', mono: 'TS', cat: 'Apparel', code: 'TSS',
    updated: '28 Jun 2026', status: 'Live', type: 'reason',
    sel: ['img', 'dmg', 'sku', 'brand'], mand: { img: true, dmg: true, sku: true, brand: false },
    reasons: [
      { id: 'r1', label: 'Product is damaged', cells: { img: true, dmg: true } },
      { id: 'r2', label: 'Wrong product delivered', cells: { img: true, sku: true, brand: true } },
      { id: 'r3', label: 'Size/fit issue', cells: { img: true, sku: true } },
    ],
  },
  {
    id: 'm_boat', name: 'boAt Lifestyle', mono: 'BO', cat: 'Electronics', code: 'BOAT',
    updated: '25 Jun 2026', status: 'Live', type: 'standard',
    sel: ['img', 'dmg', 'sku', 'qty', 'seal'], mand: { img: true, dmg: true, sku: true, qty: true, seal: true }, reasons: [],
  },
  {
    id: 'm_mama', name: 'Mamaearth', mono: 'MA', cat: 'Beauty & Personal Care', code: 'MAMA',
    updated: '22 Jun 2026', status: 'Live', type: 'standard',
    sel: ['img', 'dmg', 'seal'], mand: { img: true, dmg: true, seal: true }, reasons: [],
  },
  {
    id: 'm_sugar', name: 'Sugar Cosmetics', mono: 'SU', cat: 'Beauty & Personal Care', code: 'SUGAR',
    updated: '—', status: 'Draft', type: 'nonqc', sel: [], mand: {}, reasons: [],
  },
  {
    id: 'm_wake', name: 'Wakefit', mono: 'WA', cat: 'Home & Furniture', code: 'WAKE',
    updated: '19 Jun 2026', status: 'Live', type: 'reason',
    sel: ['img', 'dmg'], mand: { img: true, dmg: true },
    reasons: [{ id: 'r1', label: 'Product is damaged', cells: { img: true, dmg: true } }],
  },
]

let UNCONFIGURED = [
  { id: 'm_neemans', name: 'Neemans Shoes', mono: 'NE', cat: 'Footwear', code: 'NEEM' },
  { id: 'm_zivame', name: 'Zivame', mono: 'ZI', cat: 'Apparel', code: 'ZIV' },
  { id: 'm_noise', name: 'Noise', mono: 'NO', cat: 'Electronics', code: 'NOISE' },
  { id: 'm_pepper', name: 'Pepperfry', mono: 'PE', cat: 'Home & Furniture', code: 'PEPR' },
]

/* ── Courier partners ── */
/* Active couriers shown as tabs (starts with 3; scales). */
let ACTIVE_COURIERS = ['Delhivery', 'Shadowfax', 'Xpressbees']
/* Prozo parent roster to add couriers from (free-text also allowed for off-roster). */
export const COURIER_ROSTER = ['Delhivery', 'Shadowfax', 'Xpressbees', 'Bluedart', 'DTDC', 'Ekart', 'Blitz', 'ATS', 'Pikndel', 'Elastic Run', 'Go-Swift']

/* Per courier: its OWN QC parameters, each mapped one-to-one to a master param.
   cqp = Courier QC Parameter (courier's identifier) · masterId = master param it maps to. */
let COURIER_MASTER = {
  Delhivery: [
    { cqp: 'ITEM_IMAGES', masterId: 'img', field: 'image_urls', input: 'Image', q: '{"images":"<image_urls>"}' },
    { cqp: 'CHECK_DAMAGED', masterId: 'dmg', field: '-', input: 'Yes/No', q: '-' },
    { cqp: 'SKU', masterId: 'sku', field: 'sku', input: 'Text', q: '{"sku":"<sku>"}' },
    { cqp: 'QC_VERSION2', masterId: 'qty', field: 'quantity', input: 'Number', q: '{"qty":"<quantity>"}' },
    { cqp: 'DESCRIPTION', masterId: 'desc', field: 'description', input: 'Text', q: '{"descr":"<description>"}' },
    { cqp: 'CHECK_SEAL', masterId: 'seal', field: '-', input: 'Yes/No', q: '-' },
  ],
  Shadowfax: [
    { cqp: 'ITEM_IMAGES', masterId: 'img', field: 'image_urls', input: 'Image', q: '{"question":"Upload front & back image","is_mandatory":1,"value":"image_urls"}' },
    { cqp: 'CHECK_DAMAGED', masterId: 'dmg', field: '-', input: 'Yes/No', q: '{"question":"Is the item damaged?","is_mandatory":1,"value":"damaged"}' },
    { cqp: 'QUANTITY', masterId: 'qty', field: 'quantity', input: 'Number', q: '{"question":"Is quantity of item matching?","is_mandatory":1,"value":"quantity"}' },
    { cqp: 'BRAND', masterId: 'brand', field: 'brand', input: 'Text', q: '{"question":"Brand matches?","is_mandatory":0,"value":"brand"}' },
    { cqp: 'CHECK_ACCESSORIES', masterId: 'acc', field: '-', input: 'Yes/No', q: '{"question":"All accessories present?","is_mandatory":0,"value":"accessories"}' },
  ],
  Xpressbees: [
    { cqp: 'ITEM_IMAGES', masterId: 'img', field: 'image_urls', input: 'Image', q: '{"ImageUrl":"<image_urls>","TextToShow":"Front Image/Back Image"}' },
    { cqp: 'CHECK_DAMAGED', masterId: 'dmg', field: '-', input: 'Yes/No', q: '{"Label":"Is item damaged?","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'SKU', masterId: 'sku', field: 'sku', input: 'Text', q: '{"Label":"SKU: <sku>","Type":"text","ValueToCheck":"<sku>"}' },
    { cqp: 'CHECK_QUANTITY', masterId: 'qty', field: 'quantity', input: 'Number', q: '{"Label":"Qty: <quantity>","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'SIZE', masterId: 'size', field: 'size', input: 'Text', q: '{"Label":"Size: <size>","Type":"text","ValueToCheck":"<size>"}' },
    { cqp: 'COLOR', masterId: 'color', field: 'color', input: 'Text', q: '{"Label":"Colour: <color>","Type":"text","ValueToCheck":"<color>"}' },
  ],
}

/* PCIS catalog — the QC parameters each courier exposes in PCIS. These flow IN from PCIS;
   on the mapping screen we only MAP them to master params (new ones are added in PCIS, not here). */
const PCIS_CATALOG = {
  Delhivery: [
    { cqp: 'ITEM_IMAGES', field: 'image_urls', input: 'Image', q: '{"images":"<image_urls>"}' },
    { cqp: 'CHECK_DAMAGED', field: '-', input: 'Yes/No', q: '-' },
    { cqp: 'SKU', field: 'sku', input: 'Text', q: '{"sku":"<sku>"}' },
    { cqp: 'QC_VERSION2', field: 'quantity', input: 'Number', q: '{"qty":"<quantity>"}' },
    { cqp: 'DESCRIPTION', field: 'description', input: 'Text', q: '{"descr":"<description>"}' },
    { cqp: 'CHECK_SEAL', field: '-', input: 'Yes/No', q: '-' },
    { cqp: 'BRAND_VERIFY', field: 'brand', input: 'Text', q: '{"brand":"<brand>"}' },
    { cqp: 'SIZE_CHK', field: 'size', input: 'Text', q: '{"size":"<size>"}' },
    { cqp: 'COLOR_CHK', field: 'color', input: 'Text', q: '{"color":"<color>"}' },
    { cqp: 'ACCESSORIES', field: '-', input: 'Yes/No', q: '-' },
  ],
  Shadowfax: [
    { cqp: 'ITEM_IMAGES', field: 'image_urls', input: 'Image', q: '{"question":"Upload front & back image","is_mandatory":1,"value":"image_urls"}' },
    { cqp: 'CHECK_DAMAGED', field: '-', input: 'Yes/No', q: '{"question":"Is the item damaged?","is_mandatory":1,"value":"damaged"}' },
    { cqp: 'QUANTITY', field: 'quantity', input: 'Number', q: '{"question":"Is quantity of item matching?","is_mandatory":1,"value":"quantity"}' },
    { cqp: 'BRAND', field: 'brand', input: 'Text', q: '{"question":"Brand matches?","is_mandatory":0,"value":"brand"}' },
    { cqp: 'CHECK_ACCESSORIES', field: '-', input: 'Yes/No', q: '{"question":"All accessories present?","is_mandatory":0,"value":"accessories"}' },
    { cqp: 'SKU_MATCH', field: 'sku', input: 'Text', q: '{"question":"SKU matches?","is_mandatory":1,"value":"sku"}' },
    { cqp: 'SIZE', field: 'size', input: 'Text', q: '{"question":"Size matches?","is_mandatory":0,"value":"size"}' },
    { cqp: 'COLOUR', field: 'color', input: 'Text', q: '{"question":"Colour matches?","is_mandatory":0,"value":"color"}' },
    { cqp: 'SEAL_OK', field: '-', input: 'Yes/No', q: '{"question":"Seal intact?","is_mandatory":1,"value":"seal"}' },
    { cqp: 'DESCR', field: 'description', input: 'Text', q: '{"question":"Describe item","is_mandatory":0,"value":"description"}' },
  ],
  Xpressbees: [
    { cqp: 'ITEM_IMAGES', field: 'image_urls', input: 'Image', q: '{"ImageUrl":"<image_urls>","TextToShow":"Front Image/Back Image"}' },
    { cqp: 'CHECK_DAMAGED', field: '-', input: 'Yes/No', q: '{"Label":"Is item damaged?","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'SKU', field: 'sku', input: 'Text', q: '{"Label":"SKU: <sku>","Type":"text","ValueToCheck":"<sku>"}' },
    { cqp: 'CHECK_QUANTITY', field: 'quantity', input: 'Number', q: '{"Label":"Qty: <quantity>","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'SIZE', field: 'size', input: 'Text', q: '{"Label":"Size: <size>","Type":"text","ValueToCheck":"<size>"}' },
    { cqp: 'COLOR', field: 'color', input: 'Text', q: '{"Label":"Colour: <color>","Type":"text","ValueToCheck":"<color>"}' },
    { cqp: 'BRAND_CHK', field: 'brand', input: 'Text', q: '{"Label":"Brand","Type":"text","ValueToCheck":"<brand>"}' },
    { cqp: 'SEAL_CHK', field: '-', input: 'Yes/No', q: '{"Label":"Seal intact?","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'ACCESSORIES_CHK', field: '-', input: 'Yes/No', q: '{"Label":"Accessories present?","Type":"boolean","ValueToCheck":"true"}' },
    { cqp: 'DESCRIPTION', field: 'description', input: 'Text', q: '{"Label":"Description","Type":"text","ValueToCheck":"<description>"}' },
  ],
}
const pcisFor = (c) => PCIS_CATALOG[c] || []
const pcisEntry = (c, cqp) => pcisFor(c).find((x) => x.cqp === cqp) || null

function today() {
  const d = new Date()
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`
}
function slug(name) {
  const s = String(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'param'
  const exists = (cand) => MASTER_PARAMS.some((p) => p.id === cand)
  let id = s, n = 2
  while (exists(id)) { id = `${s}_${n++}` }
  return id
}

/* ── merchant-side mock ops ── */
export function mockListMerchants() {
  return { configured: clone(MERCHANTS), unconfigured: clone(UNCONFIGURED) }
}
export function mockGetMerchant(merchantId) {
  const found = MERCHANTS.find((m) => m.id === merchantId)
  if (found) return clone(found)
  const u = UNCONFIGURED.find((x) => x.id === merchantId)
  if (u) return { ...clone(u), isNew: true, type: 'reason', status: 'Draft', sel: [], mand: {}, reasons: [] }
  return null
}
export function mockListParams() {
  return clone(MASTER_PARAMS)
}
export function mockSaveMerchant(config) {
  const c = clone(config)
  if (c.type === 'nonqc') { c.sel = []; c.mand = {}; c.reasons = [] }
  if (c.status === 'Draft') c.status = 'Live'
  c.updated = today()
  delete c.isNew
  const idx = MERCHANTS.findIndex((m) => m.id === c.id)
  if (idx >= 0) MERCHANTS[idx] = c
  else { UNCONFIGURED = UNCONFIGURED.filter((u) => u.id !== c.id); MERCHANTS = [c, ...MERCHANTS] }
  return clone(c)
}
export function mockChangeRequest(merchantId, note) {
  return { merchantId, note: note || '', loggedAt: new Date().toISOString(), notified: 'rvpqc_setup.change@prozo.com' }
}

/* ── Courier QC Master mock ops ── */
/* Whole bundle the 3-step wizard reads. */
export function mockGetQcMaster() {
  return {
    masterParams: clone(MASTER_PARAMS),
    activeCouriers: clone(ACTIVE_COURIERS),
    roster: clone(COURIER_ROSTER),
    courierMaster: clone(COURIER_MASTER),
    pcisCatalog: clone(PCIS_CATALOG),
  }
}
/* add/edit a master param. Returns {ok} or {error} for the unique-name rule. */
export function mockSaveMasterParam({ mode, id, name, desc }) {
  const nm = (name || '').trim()
  if (!nm) return { error: 'Enter a parameter name.' }
  const dup = MASTER_PARAMS.some((p) => p.name.toLowerCase() === nm.toLowerCase() && !(mode === 'edit' && p.id === id))
  if (dup) return { error: `A master parameter named "${nm}" already exists. Names must be unique.` }
  if (mode === 'edit') { const p = mpById(id); if (p) { p.name = nm; p.desc = (desc || '').trim() } }
  else MASTER_PARAMS.push({ id: slug(nm), name: nm, desc: (desc || '').trim() })
  return { ok: true }
}
export function mockDeleteMasterParam(id) {
  MASTER_PARAMS = MASTER_PARAMS.filter((p) => p.id !== id)
  ACTIVE_COURIERS.forEach((c) => { COURIER_MASTER[c] = (COURIER_MASTER[c] || []).filter((r) => r.masterId !== id) })
  return { ok: true }
}
/* impact summary for the delete-confirm dialog */
export function masterParamUsage(id) {
  const couriers = ACTIVE_COURIERS.reduce((a, c) => a + (COURIER_MASTER[c] || []).filter((r) => r.masterId === id).length, 0)
  const inMerchant = MERCHANTS.some((m) => m.sel && m.sel.includes(id))
  return { couriers, inMerchant }
}
export function mockAddCourier(name) {
  const nm = (name || '').trim()
  if (!nm) return { error: 'Pick a courier from the roster or type a new name.' }
  if (ACTIVE_COURIERS.some((c) => c.toLowerCase() === nm.toLowerCase())) return { error: `${nm} is already in the active list.` }
  ACTIVE_COURIERS.push(nm)
  if (!COURIER_MASTER[nm]) COURIER_MASTER[nm] = []
  return { ok: true, courier: nm }
}
export function mockDeleteCourier(name) {
  delete COURIER_MASTER[name]
  ACTIVE_COURIERS = ACTIVE_COURIERS.filter((c) => c !== name)
  return { ok: true }
}
/* map / re-map / unmap a courier param to a master param (auto-fill from PCIS, enforce one-to-one). */
export function mockMapCourierParam(courier, masterId, cqp) {
  if (!courier) return { ok: true }
  const list = COURIER_MASTER[courier] || (COURIER_MASTER[courier] = [])
  for (let i = list.length - 1; i >= 0; i--) { if (list[i].masterId === masterId) list.splice(i, 1) }
  if (cqp) {
    for (let i = list.length - 1; i >= 0; i--) { if (list[i].cqp === cqp) list.splice(i, 1) }
    const cat = pcisEntry(courier, cqp)
    if (cat) list.push({ cqp: cat.cqp, masterId, field: cat.field, input: cat.input, q: cat.q })
  }
  return { ok: true }
}
